import React from 'react'
import StyledList from '@toasttab/shared/src/components/molecules/StyledList/StyledList'
import Animation from '@toasttab/shared/src/components/atoms/Animation/Animation'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import DescriptionMDS from '@toasttab/shared/src/components/molecules/DescriptionMDS/DescriptionMDS'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import ImageResolutions from '@toasttab/shared/src/components/atoms/Image/ImageResolutions'
import Accordion from '@toasttab/shared/src/components/molecules/Accordion/Accordion'
import Eyebrow from '@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow'
import Video from '@toasttab/shared/src/components/atoms/Video/Video'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import MainFormContentful from '@toasttab/shared/src/components/molecules/Contentful/Form/MainFormContentful'
import TwoButtons from '@toasttab/shared/src/components/molecules/TwoButtons/TwoButtons'

interface ContentColumnProps {
  type: string
  component: any
  textColor?: string
}

const Components: any = {
  styledList: StyledList,
  animation: Animation,
  heading: Typography,
  description: DescriptionMDS,
  link: TextLink,
  textLink: TextLink,
  logo: ImageResolutions,
  button: Button,
  image: ImageResolutions,
  accordion: Accordion,
  eyebrow: Eyebrow,
  video: Video,
  form: MainFormContentful,
  badge: Badge,
  twoButtons: TwoButtons
}

const getBaseProps = (type: string, component: any, textColor: string) => {
  switch (type) {
    case 'heading':
      return {
        as: component.as || 'h2',
        variation: component.variation || 'h2',
        classes: `${
          component.classes ? component.classes : 'mb-6'
        } ${textColor}`
      }
    case 'description':
      return {
        tagEl: 'div',
        variation: 'default',
        classes: `${
          component.classes ? component.classes : 'mb-4'
        } ${textColor}` // textColorClass
      }
    case 'eyebrow':
      return {
        variation: 'ds',
        classes:
          component.componentClass +
          ' overline-text inline-flex items-center mb-2'
      }
    case 'styledList':
      return {
        classes:
          !component.listStyles || component.listStyles !== 'none'
            ? ' [&>ul>li]:pl-10 ' + component.listStyles
            : ''
      }
    case 'video':
      return {
        classes: component.classes,
        imageClasses: component.imageClasses
      }
    case 'badge':
      return {
        classes: component.classes
      }
    case 'image':
      return {
        classes: component.classes
      }
    case 'badge':
      return {
        classes: component.classes
      }
    default:
      return {}
  }
}

const refactorProps = (type: string, component: any) => {
  switch (type) {
    case 'description':
      return {
        content: component.wysiwygV2
      }
    case 'textLink':
    case 'link':
      return {
        href: component.ctaLink,
        text: component.ctaLabel,
        icon: component.linkIcons,
        color: 'default' // text-color
      }
    case 'button':
      return {
        href: component.ctaLink,
        variation: component.buttonVariations,
        size: component.buttonPadding,
        fullWidth: component.buttonPadding === 'Full Width'
      }
    case 'video':
      return {
        image: component.toggleSwitch ? component.image : null,
        sectionHeading: component.sectionHeading,
        vidyardId: component.vidyardId,
        variation: component.buttonVariations,
        title: component.subtitle
      }
    case 'image':
      return {
        ...component,
        images: component,
        borderRadius: component.imageResolutions[0].borderRadius?.label || null
      }
    case 'badge':
      return {
        ...component,
        text: component.text,
        badgeColor: component.badgeColor,
        uppercase: component.uppercase
      }
    case 'twoButtons':
      return {
        ...component,
        buttons: component.children
      }
    default:
      return { ...component }
  }
}

const addChildComponents = (type: string, component: any) => {
  switch (type) {
    case 'eyebrow':
      return null
    case 'accordion':
      return component.children
    case 'button':
      return component.ctaLabel
    case 'heading':
      return component.sectionHeading
    default:
      return {}
  }
}

const ContentColumn = ({ type, component, textColor }: ContentColumnProps) => {
  const componentBaseProps = getBaseProps(type, component, textColor)
  const componentProps = refactorProps(type, component)
  const componentChildren = addChildComponents(type, component)
  if (typeof Components[type] !== 'undefined') {
    return (
      <>
        {React.createElement(
          Components[type],
          {
            ...componentBaseProps,
            ...componentProps
          },
          componentChildren
        )}
      </>
    )
  }
}

export default ContentColumn
